import {useEffect, useMemo, useState} from 'react';
import {
  CircleAlert,
  Clock3,
  Download,
  FileUp,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  TriangleAlert,
  Workflow
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {getApiErrorMessage} from '../api/client';
import {useAuth} from '../context/useAuth';
import {
  activateGroundingRevision,
  approveGrounding,
  approveWorkflowProposal,
  createWorkflow,
  deleteWorkflow,
  getCustomerTaxonomy,
  getGroundingProposal,
  getWorkflow,
  getWorkflowProposal,
  getWorkflowProposalKind,
  listGroundingRevisions,
  listWorkflows,
  previewGrounding,
  previewGroundingEntries,
  rejectGrounding,
  rejectWorkflowProposal,
  submitArchiveProposal,
  submitGrounding,
  submitGroundingEntries,
  submitWorkflowProposal,
  type ApprovedWorkflow,
  type CustomerTaxonomy,
  type GroundingPreview,
  type GroundingRevision,
  type WorkflowSummary
} from '../api/workflows';
import {ConfigureDialog} from '../components/workflows/ConfigureDialog';
import {ConfirmDialog} from '../components/workflows/ConfirmDialog';
import {CreateWorkflowDialog} from '../components/workflows/CreateWorkflowDialog';
import {GroundingEditor} from '../components/workflows/GroundingEditor';
import {TONE_BADGE} from '../lib/status';
import {HistoryPanel} from '../components/workflows/HistoryPanel';
import {ReplaceCsvSheet} from '../components/workflows/ReplaceCsvSheet';
import {ReviewPanel} from '../components/workflows/ReviewPanel';
import {RoutingPanel} from '../components/workflows/RoutingPanel';
import {SetupSteps, WorkflowStatusBadge} from '../components/workflows/SetupSteps';
import {StageSheet} from '../components/workflows/StageSheet';
import {WorkflowRail} from '../components/workflows/WorkflowRail';
import {
  CSV_HEADER,
  emptyStage,
  short,
  stageCount,
  stagedEntries,
  type ConfigDraft,
  type PendingArchive,
  type PendingConfig,
  type PendingGround,
  type Stage
} from '../components/workflows/workflowUi';

type Modal = 'create' | 'config' | 'csv' | 'stage' | 'archive' | 'delete' | null;

function escape(value: string) {
  return `"${value.replaceAll('"', '""')}"`;
}

export function WorkflowsPage() {
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState<ApprovedWorkflow | null>(null);
  const [taxonomy, setTaxonomy] = useState<CustomerTaxonomy>({pack_version: null, nodes: []});
  const [revisions, setRevisions] = useState<GroundingRevision[]>([]);
  const [pendingGround, setPendingGround] = useState<PendingGround | null>(null);
  const [pendingConfig, setPendingConfig] = useState<PendingConfig | null>(null);
  const [pendingArchive, setPendingArchive] = useState<PendingArchive | null>(null);
  const [stages, setStages] = useState<Record<string, Stage>>({});
  const [stagePreview, setStagePreview] = useState<GroundingPreview | null>(null);
  const [csv, setCsv] = useState(CSV_HEADER + '\n');
  const [csvPreview, setCsvPreview] = useState<GroundingPreview | null>(null);
  const [csvConfirm, setCsvConfirm] = useState(false);
  const [modal, setModal] = useState<Modal>(null);
  const [tab, setTab] = useState('grounding');
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const {user} = useAuth();
  const canReview = user?.role === 'admin' || user?.role === 'analyst_admin';
  const canContribute = true;

  function canDecide(author: string) {
    return user?.role === 'admin' || author.toLowerCase() !== user?.username?.toLowerCase();
  }

  useEffect(() => {
    getCustomerTaxonomy()
      .then(setTaxonomy)
      .catch((error_: unknown) => {
        setError(getApiErrorMessage(error_));
      });
    let active = true;
    listWorkflows()
      .then((rows) => {
        if (!active) {
          return;
        }

        setWorkflows(rows);
        if (rows.length > 0) {
          setSelectedId(rows[0]!.id);
        }
      })
      .catch((error_: unknown) => {
        if (active) {
          setError(getApiErrorMessage(error_));
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }

    let active = true;
    setBusy(true);
    getWorkflow(selectedId)
      .then((value) => {
        if (active) {
          setDetail(value);
        }
      })
      .catch((error_: unknown) => {
        if (active) {
          setDetail(null);
          setError(getApiErrorMessage(error_));
        }
      })
      .finally(() => {
        if (active) {
          setBusy(false);
        }
      });
    return () => {
      active = false;
    };
  }, [selectedId]);

  useEffect(() => {
    setRevisions([]);
    if (!selectedId) {
      return;
    }

    let active = true;
    listGroundingRevisions(selectedId)
      .then((rows) => {
        if (active) {
          setRevisions(rows);
        }
      })
      .catch((error_: unknown) => {
        if (active) {
          setError(getApiErrorMessage(error_));
        }
      });
    return () => {
      active = false;
    };
  }, [selectedId]);

  useEffect(() => {
    setPendingGround(null);
    setPendingConfig(null);
    setPendingArchive(null);
    const workflow = workflows.find((row) => row.id === selectedId);
    if (!selectedId || !workflow?.proposal_id || !canReview) {
      return;
    }

    let active = true;
    getGroundingProposal(selectedId, workflow.proposal_id)
      .then((proposal) => {
        if (active) {
          setPendingGround(proposal);
        }
      })
      .catch(() => undefined);
    getWorkflowProposal(selectedId, workflow.proposal_id)
      .then((proposal) => {
        if (active) {
          setPendingConfig(proposal);
        }
      })
      .catch(() => undefined);
    getWorkflowProposalKind(selectedId, workflow.proposal_id)
      .then((proposal) => {
        if (active && proposal.kind === 'archive') {
          setPendingArchive({id: workflow.proposal_id!, author: proposal.author});
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [selectedId, workflows, canReview]);

  const selectedProposalId = workflows.find((row) => row.id === selectedId)?.proposal_id ?? null;

  useEffect(() => {
    if (!selectedId || !selectedProposalId) {
      return;
    }

    let active = true;
    async function check() {
      const rows = await listWorkflows();
      const current = rows.find((row) => row.id === selectedId)?.proposal_id ?? null;
      if (!active || current === selectedProposalId) {
        return;
      }

      setWorkflows(rows);
      setDetail(await getWorkflow(selectedId));
      setRevisions(await listGroundingRevisions(selectedId));
      setNotice(
        'The proposal in review has been decided. The page now shows the current guidance.'
      );
    }

    function refresh() {
      check().catch(() => undefined);
    }

    function refreshWhenVisible() {
      if (!document.hidden) {
        refresh();
      }
    }

    const timer = setInterval(refreshWhenVisible, 30_000);
    globalThis.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      active = false;
      clearInterval(timer);
      globalThis.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [selectedId, selectedProposalId]);

  const draft = detail?.workflow.status === 'draft';
  const archived = detail?.workflow.status === 'archived';
  const hasProposal = Boolean(detail?.workflow.proposal_id);
  const stage = stages[selectedId] ?? emptyStage();
  const staged = stageCount(stage);
  const tabs = draft ? ['review', 'history'] : ['grounding', 'routing', 'review', 'history'];
  const currentTab = tabs.includes(tab) ? tab : tabs[0]!;
  const editable = canContribute && !hasProposal && !archived;
  const locked = archived
    ? 'Archived — read-only.'
    : hasProposal
      ? 'Editing is paused while a proposal is in review.'
      : canContribute
        ? null
        : 'You can view grounding but not change it.';
  const unsent = useMemo(
    () => Object.fromEntries(Object.entries(stages).map(([id, s]) => [id, stageCount(s).total])),
    [stages]
  );
  const configInitial = useMemo<ConfigDraft>(() => {
    const rootByName = taxonomy.nodes.find(
      (node) => !node.parent_concept_id && node.name === detail?.workflow.name
    );
    return {
      name: detail?.workflow.name ?? '',
      roots: detail?.current_root_concept_ids.length
        ? detail.current_root_concept_ids
        : rootByName
          ? [rootByName.concept_id]
          : [],
      routing_definition: detail?.routing_definition ?? '',
      routing_keywords: detail?.routing_keywords ?? '',
      enabled: draft ? true : (detail?.enabled ?? false),
      include_non_violative: detail?.include_non_violative ?? false
    };
  }, [detail, draft, taxonomy.nodes]);

  function rootPath(id: string) {
    return (
      detail?.nodes.find((node) => node.concept_id === id)?.path ??
      taxonomy.nodes.find((node) => node.concept_id === id)?.name ??
      id
    );
  }

  function setStage(update: (current: Stage) => Stage) {
    setStages((all) => ({...all, [selectedId]: update(all[selectedId] ?? emptyStage())}));
  }

  function clearStage() {
    setStages((all) => Object.fromEntries(Object.entries(all).filter(([id]) => id !== selectedId)));
  }

  function markProposal(proposalId: string | null, status?: string) {
    setWorkflows((rows) =>
      rows.map((w) =>
        w.id === selectedId ? {...w, proposal_id: proposalId, ...(status ? {status} : {})} : w
      )
    );
  }

  function pick(id: string) {
    setSelectedId(id);
    setNotice('');
    setError('');
    setStagePreview(null);
    setTab('grounding');
  }

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError('');
    try {
      await action();
    } catch (error_: unknown) {
      setError(getApiErrorMessage(error_));
    } finally {
      setBusy(false);
    }
  }

  async function reloadDetail() {
    setDetail(await getWorkflow(selectedId));
    setRevisions(await listGroundingRevisions(selectedId));
  }

  async function create(value: {name: string; key: string}) {
    await run(async () => {
      const row = await createWorkflow(value);
      setWorkflows((rows) => [...rows, row].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedId(row.id);
      setModal(null);
      setTab('review');
    });
  }

  async function proposeConfig(value: ConfigDraft) {
    if (!selectedId || !taxonomy.pack_version) {
      return;
    }

    await run(async () => {
      const proposal = await submitWorkflowProposal(selectedId, {
        base_revision: detail?.workflow.approved_taxonomy_revision || null,
        pack_version: taxonomy.pack_version!,
        root_concept_ids: value.roots,
        name: value.name.trim(),
        routing_definition: value.routing_definition.trim(),
        routing_keywords: value.routing_keywords.trim(),
        enabled: value.enabled,
        include_non_violative: value.include_non_violative
      });
      setModal(null);
      markProposal(proposal.id);
      setPendingConfig(proposal);
      setTab('review');
      setNotice('Configuration proposal submitted.');
    });
  }

  async function decide(kind: 'config' | 'ground' | 'archive', decision: 'approve' | 'reject') {
    const proposalId =
      kind === 'config'
        ? pendingConfig?.id
        : kind === 'ground'
          ? pendingGround?.id
          : pendingArchive?.id;
    if (!selectedId || !proposalId) {
      return;
    }

    await run(async () => {
      try {
        if (kind === 'ground') {
          await (decision === 'approve'
            ? approveGrounding(selectedId, proposalId)
            : rejectGrounding(selectedId, proposalId));
        } else {
          await (decision === 'approve'
            ? approveWorkflowProposal(selectedId, proposalId)
            : rejectWorkflowProposal(selectedId, proposalId));
        }
      } catch (error_: unknown) {
        const rows = await listWorkflows();
        if (rows.find((row) => row.id === selectedId)?.proposal_id === proposalId) {
          throw error_;
        }

        setWorkflows(rows);
        await reloadDetail();
        setNotice('This proposal was already decided. The page now shows the current guidance.');
        return;
      }

      setPendingConfig(null);
      setPendingGround(null);
      setPendingArchive(null);
      if (kind === 'archive' && decision === 'approve') {
        markProposal(null, 'archived');
        setDetail((current) =>
          current
            ? {...current, workflow: {...current.workflow, status: 'archived', proposal_id: null}}
            : current
        );
      } else {
        markProposal(null);
        await reloadDetail();
      }

      setNotice(
        decision === 'reject'
          ? 'Proposal rejected. Nothing RD serves has changed.'
          : kind === 'archive'
            ? 'Risk guidance archived.'
            : kind === 'ground'
              ? 'Grounding published. RD serves it on its next load.'
              : 'Approved. RD routes against the new revision on its next load.'
      );
    });
  }

  async function openStage() {
    setModal('stage');
    setStagePreview(null);
    await run(async () => {
      if (detail) {
        setStagePreview(
          await previewGroundingEntries(selectedId, stagedEntries(detail.grounding, stage))
        );
      }
    });
  }

  async function submitStage() {
    if (!detail || !stagePreview || stagePreview.errors.length > 0) {
      return;
    }

    await run(async () => {
      const proposal = await submitGroundingEntries(
        selectedId,
        stagedEntries(detail.grounding, stage),
        true
      );
      markProposal(proposal.id);
      clearStage();
      setStagePreview(null);
      setModal(null);
      setTab('review');
      setNotice('Submitted for independent review. RD keeps serving the current grounding.');
    });
  }

  async function checkCsv() {
    setCsvPreview(null);
    setCsvConfirm(false);
    await run(async () => {
      setCsvPreview(await previewGrounding(selectedId, csv));
    });
  }

  async function submitCsv() {
    const changed =
      csvPreview !== null && (csvPreview.changed_count > 0 || csvPreview.retired_count > 0);
    if (!csvPreview || csvPreview.errors.length > 0 || (changed && !csvConfirm)) {
      return;
    }

    await run(async () => {
      const proposal = await submitGrounding(selectedId, csv, csvConfirm);
      markProposal(proposal.id);
      setCsvPreview(null);
      setCsvConfirm(false);
      setModal(null);
      setTab('review');
      setNotice('CSV submitted for independent review.');
    });
  }

  async function restore(revisionId: string) {
    await run(async () => {
      await activateGroundingRevision(selectedId, revisionId);
      await reloadDetail();
      setNotice('Revision restored. RD serves it on its next load.');
    });
  }

  async function requestArchive() {
    await run(async () => {
      const proposal = await submitArchiveProposal(selectedId);
      setPendingArchive({id: proposal.id, author: proposal.author});
      markProposal(proposal.id);
      setModal(null);
      setTab('review');
      setNotice('Archive requested. An approver has to agree.');
    });
  }

  async function removeDraft() {
    if (!detail) {
      return;
    }

    await run(async () => {
      await deleteWorkflow(detail.workflow.id);
      setWorkflows((rows) => rows.filter((row) => row.id !== detail.workflow.id));
      setSelectedId('');
      setDetail(null);
      setModal(null);
      setNotice('Draft deleted.');
    });
  }

  function exportCsv() {
    if (!detail) {
      return;
    }

    const rows = detail.grounding.map((entry) =>
      [
        taxonomy.nodes[0]?.name || '',
        detail.workflow.key,
        entry.id,
        entry.concept_id,
        rootPath(entry.concept_id),
        entry.section,
        entry.polarity,
        entry.modalities.join('|'),
        entry.content,
        'approved'
      ]
        .map(escape)
        .join(',')
    );
    const blob = new Blob([[CSV_HEADER, ...rows].join('\r\n')], {type: 'text/csv;charset=utf-8'});
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${detail.workflow.key}-grounding.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  function startCsvFromCurrent() {
    if (!detail) {
      return;
    }

    setCsv(
      [
        CSV_HEADER,
        ...detail.grounding.map((entry) =>
          [
            taxonomy.nodes[0]?.name || '',
            detail.workflow.key,
            entry.id,
            entry.concept_id,
            rootPath(entry.concept_id),
            entry.section,
            entry.polarity,
            entry.modalities.join('|'),
            entry.content,
            'approved'
          ]
            .map(escape)
            .join(',')
        )
      ].join('\n')
    );
    setCsvPreview(null);
  }

  const filtered = workflows.filter((w) =>
    `${w.name} ${w.key}`.toLowerCase().includes(query.toLowerCase())
  );

  const createButton = canContribute ? (
    <Button
      size="sm"
      onClick={() => {
        setModal('create');
      }}>
      <Plus aria-hidden className="size-4" />
      Create guidance
    </Button>
  ) : null;

  return (
    <main className="flex min-h-0 flex-1 flex-col">
      <div className="border-border flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
        <div>
          <p className="text-muted-foreground text-xs">
            {taxonomy.pack_version ? `Risk guidance · ${taxonomy.pack_version}` : 'Risk guidance'}
          </p>
          <h2 className="font-heading text-lg font-semibold">Risk guidance</h2>
        </div>
      </div>
      {error ? (
        <Alert variant="destructive" className="mx-5 mt-3 w-auto">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      {notice ? (
        <Alert role="status" className="mx-5 mt-3 w-auto">
          <AlertTitle>{notice}</AlertTitle>
        </Alert>
      ) : null}
      {workflows.length === 0 ? (
        <div className="grid flex-1 place-items-center p-6">
          <Empty>
            <EmptyHeader>
              <Workflow aria-hidden className="text-muted-foreground size-8" />
              <EmptyTitle>No risk guidance configured</EmptyTitle>
              <EmptyDescription>
                Create customer risk guidance, approve its first scope and routing, then add
                reviewed grounding.
              </EmptyDescription>
            </EmptyHeader>
            {createButton}
          </Empty>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <WorkflowRail
            workflows={filtered}
            selectedId={selectedId}
            query={query}
            unsent={unsent}
            onQuery={setQuery}
            onPick={pick}
            onCreate={
              canContribute
                ? () => {
                    setModal('create');
                  }
                : undefined
            }
          />
          {detail ? (
            <section
              aria-label={detail.workflow.name}
              className="flex min-h-0 min-w-0 flex-1 flex-col">
              <div className="border-border flex flex-wrap items-start justify-between gap-4 border-b px-5 py-4">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <h3 className="flex flex-wrap items-center gap-2 text-xl font-semibold">
                    {detail.workflow.name}
                    <WorkflowStatusBadge status={detail.workflow.status} enabled={detail.enabled} />
                    {hasProposal ? (
                      <Badge variant="secondary">
                        <Clock3 aria-hidden className="size-3" />
                        In review
                      </Badge>
                    ) : null}
                    {detail.workflow.taxonomy_outdated ? (
                      <Badge variant="outline" className="text-destructive">
                        <TriangleAlert aria-hidden className="size-3" />
                        Outdated
                      </Badge>
                    ) : null}
                  </h3>
                  <p className="text-muted-foreground flex flex-wrap gap-x-4 text-xs">
                    <span>
                      Key <code className="bg-muted rounded px-1">{detail.workflow.key}</code>
                    </span>
                    <span>
                      {archived
                        ? 'Archived — no longer served to RD.'
                        : draft
                          ? 'Not served to RD yet'
                          : `Serving revision ${short(detail.workflow.approved_taxonomy_revision)}`}
                    </span>
                  </p>
                  {detail.workflow.taxonomy_outdated && !hasProposal ? (
                    <p className="text-muted-foreground text-xs">
                      The customer taxonomy changed since this revision was approved. Edit the
                      configuration to propose a revision against it.
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  {busy ? <span className="text-muted-foreground text-sm">Loading…</span> : null}
                  {canContribute && !archived && !draft ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={hasProposal}
                      title={hasProposal ? 'Resolve the pending proposal first' : undefined}
                      onClick={() => {
                        setModal('config');
                      }}>
                      <Pencil aria-hidden className="size-4" />
                      Edit configuration
                    </Button>
                  ) : null}
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      render={
                        <Button size="icon-sm" variant="secondary" aria-label="More actions" />
                      }>
                      <MoreHorizontal aria-hidden className="size-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-56">
                      {draft ? null : (
                        <DropdownMenuItem onClick={exportCsv}>
                          <Download aria-hidden className="size-4" />
                          Export grounding CSV
                        </DropdownMenuItem>
                      )}
                      {!draft && canContribute && !archived ? (
                        <DropdownMenuItem
                          disabled={hasProposal || staged.total > 0}
                          onClick={() => {
                            setModal('csv');
                          }}>
                          <FileUp aria-hidden className="size-4" />
                          Replace from CSV…
                        </DropdownMenuItem>
                      ) : null}
                      {canContribute && !archived ? (
                        <>
                          <DropdownMenuSeparator />
                          {draft ? (
                            <DropdownMenuItem
                              variant="destructive"
                              onClick={() => {
                                setModal('delete');
                              }}>
                              <Trash2 aria-hidden className="size-4" />
                              Delete draft
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              variant="destructive"
                              disabled={hasProposal}
                              onClick={() => {
                                setModal('archive');
                              }}>
                              Request archive…
                            </DropdownMenuItem>
                          )}
                        </>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              {staged.total > 0 ? (
                <div
                  role="region"
                  aria-label="Unsent changes"
                  className={`${TONE_BADGE.warning} border-border flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-5 py-2`}>
                  <CircleAlert aria-hidden className="size-4 shrink-0" />
                  <span className="font-medium">
                    Changes pending · {staged.total} unsent change{staged.total > 1 ? 's' : ''}
                  </span>
                  <span className="text-xs">
                    {[
                      staged.added ? `${staged.added} added` : '',
                      staged.changed ? `${staged.changed} edited` : '',
                      staged.retired ? `${staged.retired} retiring` : ''
                    ]
                      .filter(Boolean)
                      .join(' · ')}{' '}
                    — not sent for review yet; nothing changes for RD until approved
                  </span>
                  <span className="ml-auto flex gap-2">
                    <Button size="sm" variant="secondary" onClick={clearStage}>
                      Discard
                    </Button>
                    <Button size="sm" disabled={busy} onClick={() => void openStage()}>
                      Review and submit
                    </Button>
                  </span>
                </div>
              ) : null}
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                <div className="flex flex-1 flex-col gap-4 px-5 pt-3 pb-6">
                  {hasProposal && currentTab !== 'review' && !draft ? (
                    <Alert>
                      <Clock3 aria-hidden />
                      <AlertTitle>Proposal waiting for review</AlertTitle>
                      <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                        <span>RD keeps serving what’s shown here until it’s decided.</span>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setTab('review');
                          }}>
                          Open review
                        </Button>
                      </AlertDescription>
                    </Alert>
                  ) : null}
                  {draft ? (
                    <SetupSteps
                      workflowKey={detail.workflow.key}
                      hasProposal={hasProposal}
                      proposalAuthor={pendingConfig?.author ?? null}
                      canContribute={canContribute}
                      onConfigure={() => {
                        setModal('config');
                      }}
                      onReview={() => {
                        setTab('review');
                      }}
                    />
                  ) : null}
                  <Tabs value={currentTab} className="flex flex-col gap-3" onValueChange={setTab}>
                    <TabsList className="w-fit">
                      {draft ? null : (
                        <TabsTrigger value="grounding">
                          Grounding <Badge variant="secondary">{detail.grounding.length}</Badge>
                        </TabsTrigger>
                      )}
                      {draft ? null : <TabsTrigger value="routing">Routing</TabsTrigger>}
                      <TabsTrigger value="review">
                        Review
                        {hasProposal ? (
                          <Badge variant="secondary">
                            <Clock3 aria-hidden className="size-3" />1
                          </Badge>
                        ) : null}
                      </TabsTrigger>
                      <TabsTrigger value="history">History</TabsTrigger>
                    </TabsList>
                    {draft ? null : (
                      <TabsContent value="grounding" className="m-0">
                        <GroundingEditor
                          workflow={detail}
                          editable={editable}
                          locked={locked}
                          stage={stage}
                          setStage={setStage}
                        />
                      </TabsContent>
                    )}
                    {draft ? null : (
                      <TabsContent value="routing" className="m-0">
                        <RoutingPanel
                          detail={detail}
                          editable={editable}
                          onEdit={() => {
                            setModal('config');
                          }}
                        />
                      </TabsContent>
                    )}
                    <TabsContent value="review" className="m-0">
                      <ReviewPanel
                        workflowName={detail.workflow.name}
                        hasProposal={hasProposal}
                        pendingConfig={pendingConfig}
                        pendingGround={pendingGround}
                        pendingArchive={pendingArchive}
                        currentEntries={detail.grounding}
                        rootPath={rootPath}
                        taxonomyChanges={detail.taxonomy_changes}
                        canReview={canReview}
                        canDecide={canDecide}
                        viewer={user?.username ?? ''}
                        busy={busy}
                        onDecide={(kind, decision) => {
                          void decide(kind, decision);
                        }}
                      />
                    </TabsContent>
                    <TabsContent value="history" className="m-0">
                      <HistoryPanel
                        revisions={revisions}
                        canRestore={canReview ? !hasProposal && !archived : false}
                        busy={busy}
                        onRestore={(id) => {
                          void restore(id);
                        }}
                      />
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </section>
          ) : null}
        </div>
      )}
      <CreateWorkflowDialog
        open={modal === 'create'}
        busy={busy}
        existingKeys={workflows.map((w) => w.key)}
        onClose={() => {
          setModal(null);
        }}
        onCreate={(value) => void create(value)}
      />
      <ConfigureDialog
        open={modal === 'config'}
        busy={busy}
        first={!detail?.workflow.approved_taxonomy_revision}
        workflowKey={detail?.workflow.key ?? ''}
        revision={detail?.workflow.approved_taxonomy_revision ?? null}
        taxonomy={taxonomy}
        initial={configInitial}
        outsideScopeCount={0}
        taxonomyChanges={detail?.taxonomy_changes ?? []}
        onClose={() => {
          setModal(null);
        }}
        onSubmit={(value) => void proposeConfig(value)}
      />
      {detail ? (
        <>
          <StageSheet
            open={modal === 'stage'}
            workflow={detail}
            stage={stage}
            preview={stagePreview}
            busy={busy}
            onClose={() => {
              setModal(null);
            }}
            onSubmit={() => void submitStage()}
          />
          <ReplaceCsvSheet
            open={modal === 'csv'}
            busy={busy}
            blocked={hasProposal}
            workflowName={detail.workflow.name}
            workflowKey={detail.workflow.key}
            csv={csv}
            preview={csvPreview}
            confirm={csvConfirm}
            onCsvChange={(value) => {
              setCsv(value);
              setCsvPreview(null);
            }}
            onConfirmChange={setCsvConfirm}
            onCheck={() => void checkCsv()}
            onSubmit={() => void submitCsv()}
            onStartFromCurrent={startCsvFromCurrent}
            onClose={() => {
              setModal(null);
            }}
          />
          <ConfirmDialog
            open={modal === 'archive'}
            title={`Request archive of ${detail.workflow.name}?`}
            body="An approver has to agree. Until then RD keeps routing to it as normal."
            action="Request archive"
            onConfirm={() => void requestArchive()}
            onClose={() => {
              setModal(null);
            }}
          />
          <ConfirmDialog
            destructive
            open={modal === 'delete'}
            title={`Delete draft “${detail.workflow.name}”?`}
            body="Any pending or rejected proposals and their history are permanently deleted too. This can’t be undone."
            action="Delete draft"
            onConfirm={() => void removeDraft()}
            onClose={() => {
              setModal(null);
            }}
          />
        </>
      ) : null}
    </main>
  );
}
